'use client';
import React, { useState } from 'react';
import { Flame, Zap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

const SUPPORT_LIMIT = 5;

interface SupportSectionProps {
  postId: string;
  supportCount: number;
  onSupportSuccess: (newSupportCount: number, newAgreeCount: number) => void;
  onTopSupportersRefresh?: () => void;
}

type SupportState = 'idle' | 'loading' | 'success' | 'limit';

function getRankMessage(rank: number | null): string | null {
  if (rank === null || rank === undefined) return null;
  if (rank === 1) return "🔥 You're now a top supporter!";
  if (rank <= 3) return `🎯 You're now #${rank} supporter!`;
  return null;
}

export default function SupportSection({ postId, supportCount, onSupportSuccess, onTopSupportersRefresh }: SupportSectionProps) {
  const { user } = useAuth();
  const router = useRouter();
  const supabase = createClient();
  const [state, setState] = useState<SupportState>(supportCount >= SUPPORT_LIMIT ? 'limit' : 'idle');
  const [lastAdded, setLastAdded] = useState<number>(0);
  const [userRank, setUserRank] = useState<number | null>(null);

  const remaining = SUPPORT_LIMIT - supportCount;
  const isAtLimit = supportCount >= SUPPORT_LIMIT;

  const handleSupport = async (combo = false) => {
    if (!user) {
      router.push('/sign-up-login-screen');
      return;
    }
    if (isAtLimit) return;

    setState('loading');
    try {
      const { data, error } = await supabase.rpc('apply_support', {
        p_user_id: user.id,
        p_post_id: postId,
        p_combo: combo,
      });

      if (error) {
        console.error('Support RPC error:', error.message);
        setState(isAtLimit ? 'limit' : 'idle');
        return;
      }

      if (data?.error) {
        console.error('Support logic error:', data.error);
        setState(isAtLimit ? 'limit' : 'idle');
        return;
      }

      setLastAdded(data.votes_added);
      setUserRank(data.user_rank ?? null);
      onSupportSuccess(data.support_count, data.agree_count);
      onTopSupportersRefresh?.();
      setState(data.support_count >= SUPPORT_LIMIT ? 'limit' : 'success');
    } catch (err) {
      console.error('Support unexpected error:', err);
      setState(isAtLimit ? 'limit' : 'idle');
    }
  };

  const handleSupportAgain = () => {
    setState('idle');
  };

  // Limit reached state
  if (state === 'limit' || isAtLimit) {
    return (
      <div className="mt-3 px-3 py-2 bg-muted rounded-lg border border-border">
        <p className="text-xs text-muted-foreground font-medium text-center">
          {supportCount} people backed this · limit reached
        </p>
      </div>
    );
  }

  // After purchase success state
  if (state === 'success') {
    const rankMsg = getRankMessage(userRank);
    return (
      <div className="mt-3 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg">
        {rankMsg ? (
          <p className="text-xs font-bold text-green-800 mb-1">{rankMsg}</p>
        ) : null}
        <p className="text-xs font-semibold text-green-800 mb-2">
          You just moved up. <span className="font-normal text-green-700">+{lastAdded} votes added.</span>
        </p>
        <div className="flex items-center gap-2">
          {(SUPPORT_LIMIT - supportCount) > 0 ? (
            <button
              onClick={handleSupportAgain}
              className="flex items-center gap-1 px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors active:scale-95"
            >
              <Flame size={11} />
              Do it again
            </button>
          ) : null}
          {(SUPPORT_LIMIT - supportCount) > 0 ? (
            <button
              onClick={handleSupportAgain}
              className="flex items-center gap-1 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-lg transition-colors active:scale-95"
            >
              <Zap size={11} />
              Push more
            </button>
          ) : null}
          <button
            onClick={() => setState('limit')}
            className="px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
          >
            Keep going
          </button>
          <span className="ml-auto text-xs text-muted-foreground">
            {SUPPORT_LIMIT - supportCount}/{SUPPORT_LIMIT} left
          </span>
        </div>
      </div>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <div className="mt-3 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs text-orange-700 font-medium">Processing…</span>
        </div>
      </div>
    );
  }

  // Idle / default state
  return (
    <div className="mt-3 px-3 py-2.5 bg-orange-50 border border-orange-100 rounded-lg">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Flame size={13} className="text-orange-500" />
          <span className="text-xs font-semibold text-orange-800">Back this</span>
          <span className="text-xs text-orange-600 font-medium">· +10 votes</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {supportCount} people backed this
        </span>
      </div>

      {/* Urgency text */}
      {remaining <= 2 && (
        <p className="text-xs font-semibold text-red-600 mb-2">
          🔥 Only {remaining} left
        </p>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => handleSupport(false)}
          className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors active:scale-95 shadow-sm"
        >
          <Flame size={11} />
          Back this · $1
        </button>

        {remaining > 1 && (
          <button
            onClick={() => handleSupport(true)}
            className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-sm font-bold rounded-lg transition-all active:scale-95 shadow-sm"
          >
            <Zap size={13} />
            Dominate this
          </button>
        )}
      </div>
    </div>
  );
}
